/**
 * API Stats Utilities
 *
 * Handles API latency tracking, logging, and metrics collection
 * for performance monitoring and debugging.
 *
 * Environment Variables:
 * - API_STATS_ENABLED: Set to 'true' to enable API stats logging (default: false)
 *
 * Usage:
 * Set API_STATS_ENABLED=true in your environment to enable API stats logging
 * Example: API_STATS_ENABLED=true npm run dev
 */

import { generateTraceId, log } from '@core/services/logger.service'

// Environment variable to control API stats logging
const API_STATS_ENABLED = true //process.env.API_STATS_ENABLED === 'true'

/**
 * Check if API stats logging is enabled
 */
export function isApiStatsEnabled(): boolean {
  return API_STATS_ENABLED
}

// Extend globalThis to include our metrics
declare global {
  var apiLatencyMetrics: ApiLatencyLog[] | undefined
}

export interface ApiLatencyLog {
  requestId: string
  timestamp: string
  startTime: string
  endTime: string
  method: string
  endpoint: string
  service: string
  url: string
  duration: string
  apiCallDuration?: string
  baseServiceOverhead?: string
  preRequestDuration?: string
  postRequestDuration?: string
  status: number
  success: boolean
  error: string | null
  performance: string
}

export interface ApiLatencyMetrics {
  totalRequests: number
  averageLatency: number
  slowestRequests: any[]
  serviceBreakdown: Record<string, { count: number; avgLatency: number }>
  performanceBreakdown: Record<string, number>
}

/**
 * Sanitize headers to remove sensitive information
 */
function sanitizeHeaders(headers: Record<string, string>): Record<string, string> {
  const sensitiveKeys = ['cookie', 'x-api-key', 'token', 'password', 'secret']
  const sanitized: Record<string, string> = {}

  Object.entries(headers).forEach(([key, value]) => {
    const lowerKey = key.toLowerCase()
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]'
    } else {
      sanitized[key] = value
    }
  })

  return sanitized
}

/**
 * Track API latency for server-side requests
 */
export function trackApiLatency(
  config: any,
  status: number,
  errorMessage: string | null,
  errorDetails?: {
    requestUrl?: string
    requestHeaders?: Record<string, string>
    responseData?: unknown
    responseHeaders?: Record<string, string>
  }
): void {
  // Only track server-side requests and if API stats are enabled
  if (!process.server || !config?.metadata?.startTime || !API_STATS_ENABLED) {
    return
  }

  const endTime = performance.now()
  const duration = Math.round(endTime - config.metadata.startTime)
  const apiCallDuration = config.metadata.apiCallDuration
  const preRequestDuration = config.metadata.preRequestDuration
  const requestId = config.metadata.requestId || 'unknown'
  let postRequestDuration: number | undefined

  // Extract meaningful endpoint info
  const url = config.url || 'unknown'
  const method = config.method?.toUpperCase() || 'UNKNOWN'

  // Parse endpoint for better categorization
  const endpoint = parseEndpoint(url)
  const service = identifyService(url)

  // Create latency log entry
  const now = new Date()
  const startTime = new Date(now.getTime() - duration) // Approximate start time

  const latencyLog: ApiLatencyLog = {
    requestId,
    timestamp: now.toISOString(), // Request completion time
    startTime: startTime.toISOString(), // Approximate request start time
    endTime: now.toISOString(), // Request end time (same as timestamp)
    method,
    endpoint,
    service,
    url: url,
    duration: `${duration}ms`,
    status,
    success: status >= 200 && status < 300,
    error: errorMessage,
    // Add performance categorization
    performance: categorizePerformance(duration),
  }

  if (apiCallDuration !== undefined && preRequestDuration !== undefined) {
    postRequestDuration = duration - apiCallDuration - preRequestDuration
  }

  // Determine log level based on success and performance
  let logLevel: 'info' | 'warn' | 'error'
  if (!latencyLog.success) {
    logLevel = 'error'
  } else if (duration > 2000) {
    logLevel = 'warn'
  } else {
    logLevel = 'info'
  }

  // Create structured log message for GCP
  const logMessage = `API Request: ${method} ${endpoint}`

  // Base payload structure
  const basePayload = {
    url: latencyLog.url,
    timestamp: latencyLog.timestamp,
    startTime: latencyLog.startTime,
    endTime: latencyLog.endTime,
    error: latencyLog.error,
  }

  // Enhanced payload for errors with request/response details
  const enhancedPayload =
    !latencyLog.success && errorDetails
      ? {
          ...basePayload,
          requestDetails: {
            url: errorDetails.requestUrl || url,
            headers: sanitizeHeaders(errorDetails.requestHeaders || config.headers || {}),
          },
          responseDetails: {
            data: errorDetails.responseData,
            headers: sanitizeHeaders(errorDetails.responseHeaders || {}),
          },
        }
      : basePayload

  const logData = {
    labels: {
      request_id: requestId,
      method,
      endpoint,
      service,
      trace_context: config.headers['x-trace-context'],
      duration_ms: duration.toString(),
      api_call_duration_ms: apiCallDuration?.toString(),
      base_service_overhead_ms: apiCallDuration !== undefined ? (duration - apiCallDuration).toString() : undefined,
      pre_request_duration_ms: preRequestDuration?.toString(),
      post_request_duration_ms: postRequestDuration?.toString(),
      performance: latencyLog.performance,
      success: latencyLog.success.toString(),
      status: status.toString(),
    },
    payload: enhancedPayload,
  }

  // Generate a trace ID for this request
  const traceId = generateTraceId()

  // Log based on level
  if (logLevel === 'error') {
    log.error(logMessage, logData, traceId)
  } else if (logLevel === 'warn') {
    log.warn(logMessage, logData, traceId)
  } else {
    log.info(logMessage, logData, traceId)
  }

  // Store in global metrics if available (for aggregation)
  storeLatencyMetrics(latencyLog)
}

/**
 * Parse endpoint to extract meaningful path
 */
function parseEndpoint(url: string): string {
  try {
    const urlObj = new URL(url)
    const path = urlObj.pathname

    // Extract key parts of the path
    const segments = path.split('/').filter(Boolean)

    if (segments.length === 0) return '/'

    // Return the most relevant part (usually the last 2-3 segments)
    if (segments.length <= 3) {
      return `/${segments.join('/')}`
    }

    // For longer paths, return the last 2 segments
    return `/${segments.slice(-2).join('/')}`
  } catch {
    return url
  }
}

/**
 * Identify service from URL
 */
function identifyService(url: string): string {
  if (url.includes('/communities/')) return 'communities'
  if (url.includes('/community-analytics/')) return 'community-analytics'
  if (url.includes('/courses/')) return 'courses'
  if (url.includes('/membership/')) return 'membership'
  if (url.includes('/clientclub/')) return 'clientclub'
  if (url.includes('/gokollab/')) return 'gokollab'
  if (url.includes('/auth/')) return 'auth'
  if (url.includes('/users/')) return 'users'
  return 'unknown'
}

/**
 * Categorize performance based on duration
 */
function categorizePerformance(duration: number): string {
  if (duration < 200) return 'excellent'
  if (duration < 500) return 'good'
  if (duration < 1000) return 'acceptable'
  if (duration < 2000) return 'slow'
  return 'very-slow'
}

/**
 * Store latency metrics for aggregation
 */
function storeLatencyMetrics(log: ApiLatencyLog): void {
  // Store in global object for potential aggregation
  if (typeof globalThis !== 'undefined') {
    globalThis.apiLatencyMetrics = globalThis.apiLatencyMetrics || []
    globalThis.apiLatencyMetrics.push(log)

    // Keep only last 100 entries to prevent memory leaks
    if (globalThis.apiLatencyMetrics.length > 100) {
      globalThis.apiLatencyMetrics = globalThis.apiLatencyMetrics.slice(-100)
    }
  }
}

/**
 * Get API latency metrics summary
 * Useful for debugging and performance analysis
 */
export function getLatencyMetrics(): ApiLatencyMetrics {
  const metrics = globalThis.apiLatencyMetrics || []

  if (metrics.length === 0) {
    return {
      totalRequests: 0,
      averageLatency: 0,
      slowestRequests: [],
      serviceBreakdown: {},
      performanceBreakdown: {},
    }
  }

  // Calculate average latency
  const totalLatency = metrics.reduce((sum: number, log: any) => {
    const duration = parseInt(log.duration.replace('ms', ''))
    return sum + duration
  }, 0)
  const averageLatency = Math.round(totalLatency / metrics.length)

  // Get slowest requests (top 10)
  const slowestRequests = metrics
    .map((log: any) => ({
      ...log,
      durationMs: parseInt(log.duration.replace('ms', '')),
    }))
    .sort((a: any, b: any) => b.durationMs - a.durationMs)
    .slice(0, 10)

  // Service breakdown
  const serviceBreakdown: Record<string, { count: number; totalLatency: number }> = {}
  metrics.forEach((log: any) => {
    const service = log.service
    const duration = parseInt(log.duration.replace('ms', ''))

    if (!serviceBreakdown[service]) {
      serviceBreakdown[service] = { count: 0, totalLatency: 0 }
    }
    serviceBreakdown[service].count++
    serviceBreakdown[service].totalLatency += duration
  })

  // Convert to average latency per service
  const serviceBreakdownWithAvg = Object.entries(serviceBreakdown).reduce(
    (acc, [service, data]) => {
      acc[service] = {
        count: data.count,
        avgLatency: Math.round(data.totalLatency / data.count),
      }
      return acc
    },
    {} as Record<string, { count: number; avgLatency: number }>
  )

  // Performance breakdown
  const performanceBreakdown: Record<string, number> = {}
  metrics.forEach((log: any) => {
    const perf = log.performance
    performanceBreakdown[perf] = (performanceBreakdown[perf] || 0) + 1
  })

  return {
    totalRequests: metrics.length,
    averageLatency,
    slowestRequests,
    serviceBreakdown: serviceBreakdownWithAvg,
    performanceBreakdown,
  }
}

/**
 * Clear latency metrics
 */
export function clearLatencyMetrics(): void {
  if (typeof globalThis !== 'undefined') {
    globalThis.apiLatencyMetrics = []
  }
}

/**
 * Print a summary table of recent API requests
 * Useful for debugging and performance analysis
 */
export function printLatencySummary(): void {
  const metrics = getLatencyMetrics()

  if (metrics.totalRequests === 0) {
    log.info('API Latency Summary: No requests recorded', {
      labels: {
        operation: 'latency_summary',
        total_requests: '0',
      },
    })
    return
  }

  log.info('API Latency Summary', {
    labels: {
      operation: 'latency_summary',
      total_requests: metrics.totalRequests.toString(),
      average_latency_ms: metrics.averageLatency.toString(),
    },
    payload: {
      overallStats: {
        totalRequests: metrics.totalRequests,
        averageLatency: metrics.averageLatency,
      },
      serviceBreakdown: metrics.serviceBreakdown,
      performanceBreakdown: metrics.performanceBreakdown,
      slowestRequests: metrics.slowestRequests.slice(0, 5),
    },
  })
}

/**
 * Add request metadata for timing
 */
export function addRequestMetadata(config: any): void {
  if (process.server && API_STATS_ENABLED) {
    config.metadata = config.metadata || {}
    config.metadata.startTime = performance.now()
    config.metadata.requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}

/**
 * Test function to demonstrate the API latency logging
 * This can be used for testing or demonstration purposes
 */
export function testLatencyLogging(): void {
  log.info('Testing API Latency Logging', {
    labels: {
      operation: 'test_latency_logging',
      test_mode: 'true',
    },
  })

  // Simulate different types of requests
  const testRequests = [
    {
      config: {
        url: 'https://api.example.com/communities/groups/123/posts',
        method: 'GET',
        metadata: { startTime: performance.now() - 150, requestId: 'test_req_1' },
      },
      status: 200,
      errorMessage: null,
    },
    {
      config: {
        url: 'https://api.example.com/courses/products/456/lessons',
        method: 'POST',
        metadata: { startTime: performance.now() - 2500, requestId: 'test_req_2' },
      },
      status: 201,
      errorMessage: null,
    },
    {
      config: {
        url: 'https://api.example.com/auth/login',
        method: 'POST',
        metadata: { startTime: performance.now() - 800, requestId: 'test_req_3' },
      },
      status: 401,
      errorMessage: 'Invalid credentials',
    },
  ]

  testRequests.forEach((req, index) => {
    log.info(`Test Request ${index + 1}`, {
      labels: {
        operation: 'test_request',
        test_index: (index + 1).toString(),
      },
      payload: {
        url: req.config.url,
        method: req.config.method,
        expectedStatus: req.status,
        hasError: !!req.errorMessage,
      },
    })
    trackApiLatency(
      req.config,
      req.status,
      req.errorMessage,
      req.errorMessage
        ? {
            requestUrl: req.config.url,
            requestHeaders: {},
            responseData: { message: req.errorMessage },
            responseHeaders: {},
          }
        : undefined
    )
  })

  log.info('Test Summary Complete', {
    labels: {
      operation: 'test_summary',
      test_mode: 'true',
    },
  })
  printLatencySummary()
}
